<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <h5 class="mb-2">
          <feather-icon
            icon="UploadCloudIcon"
            size="18"
            class="mr-50 text-primary"
          />
          {{ $t('modules.products.import.steps.upload.title') }}
        </h5>
        <p class="text-muted mb-2">
          {{ $t('modules.products.import.steps.upload.description') }}
        </p>

        <!-- Drop zone -->
        <div
          class="import-dropzone"
          :class="{ 'is-dragover': isDragover, 'has-file': !!selectedFile }"
          @dragover.prevent="isDragover = true"
          @dragleave.prevent="isDragover = false"
          @drop.prevent="onFileDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,.xls"
            class="d-none"
            @change="onFileChange"
          >

          <div
            v-if="!selectedFile"
            class="dropzone-placeholder text-center py-4"
          >
            <feather-icon
              icon="FileTextIcon"
              size="48"
              class="text-muted mb-1"
            />
            <p class="mb-0 font-weight-bold">
              {{ $t('modules.products.import.steps.upload.drop_or_click') }}
            </p>
            <small class="text-muted">
              {{ $t('modules.products.import.steps.upload.supported_formats') }}
            </small>
          </div>

          <div
            v-else
            class="dropzone-file-info text-center py-4"
          >
            <feather-icon
              :icon="fileIcon"
              size="48"
              class="text-success mb-1"
            />
            <p class="mb-0 font-weight-bold">
              {{ selectedFile.name }}
            </p>
            <small class="text-muted">
              {{ formatFileSize(selectedFile.size) }}
            </small>
            <div class="mt-75">
              <b-button
                variant="outline-danger"
                size="sm"
                @click.stop="clearFile"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('Remove') }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- Header row configuration -->
        <b-card
          class="mt-2 border"
          no-body
        >
          <b-card-body class="py-1">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <p class="mb-0 font-weight-bold">
                  {{ $t('modules.products.import.steps.upload.header_row_label') }}
                </p>
                <small class="text-muted">
                  {{ $t('modules.products.import.steps.upload.header_row_hint') }}
                </small>
              </div>
              <b-form-checkbox
                v-model="localHasHeaderRow"
                switch
                class="ml-1"
              />
            </div>
          </b-card-body>
        </b-card>

        <!-- Validation error -->
        <b-alert
          v-if="error"
          show
          variant="danger"
          class="mt-1"
        >
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          {{ error }}
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Actions -->
    <div class="d-flex justify-content-end mt-1">
      <b-button
        variant="primary"
        :disabled="!selectedFile || isLoading"
        @click="onClickNext"
      >
        <b-spinner
          v-if="isLoading"
          small
          class="mr-50"
        />
        <span v-else>
          {{ isLoading ? $t('modules.products.import.actions.parsing') : $t('modules.products.import.actions.next') }}
          <feather-icon
            :icon="$i18n.locale === 'ar' ? 'ArrowLeftIcon' : 'ArrowRightIcon'"
            class="ml-50"
          />
        </span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BAlert, BButton, BCard, BCardBody, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { ref, computed, watch } from 'vue'

export default {
  components: {
    BCard,
    BCardBody,
    BAlert,
    BButton,
    BFormCheckbox,
    BSpinner,
  },

  props: {
    hasHeaderRow: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },

  emits: ['next', 'update:hasHeaderRow'],

  setup(props, { emit }) {
    const fileInput = ref(null)
    const selectedFile = ref(null)
    const isDragover = ref(false)
    const localHasHeaderRow = ref(props.hasHeaderRow)

    watch(localHasHeaderRow, val => emit('update:hasHeaderRow', val))

    const fileIcon = computed(() => {
      if (!selectedFile.value) return 'FileIcon'
      const name = selectedFile.value.name.toLowerCase()
      if (name.endsWith('.csv')) return 'FileTextIcon'
      return 'FileIcon'
    })

    const formatFileSize = bytes => {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const validateFile = file => {
      const allowed = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      const ext = file.name.split('.').pop().toLowerCase()
      return allowed.includes(file.type) || ['csv', 'xlsx', 'xls'].includes(ext)
    }

    const setFile = file => {
      if (!validateFile(file)) return
      selectedFile.value = file
    }

    const onFileChange = e => {
      const file = e.target.files[0]
      if (file) setFile(file)
    }

    const onFileDrop = e => {
      isDragover.value = false
      const file = e.dataTransfer.files[0]
      if (file) setFile(file)
    }

    const clearFile = () => {
      selectedFile.value = null
      if (fileInput.value) fileInput.value.value = ''
    }

    const onClickNext = () => {
      if (!selectedFile.value) return
      emit('next', { file: selectedFile.value, hasHeaderRow: localHasHeaderRow.value })
    }

    return {
      fileInput,
      selectedFile,
      isDragover,
      localHasHeaderRow,
      fileIcon,
      formatFileSize,
      onFileChange,
      onFileDrop,
      clearFile,
      onClickNext,
    }
  },
}
</script>

<style lang="scss" scoped>
.import-dropzone {
  border: 2px dashed #d8d6de;
  border-radius: 0.428rem;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover,
  &.is-dragover {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.04);
  }

  &.has-file {
    border-color: #28c76f;
    background-color: rgba(40, 199, 111, 0.04);
  }
}

.dark-layout .import-dropzone {
  border-color: #3b4253;

  &:hover,
  &.is-dragover {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.08);
  }

  &.has-file {
    border-color: #28c76f;
    background-color: rgba(40, 199, 111, 0.08);
  }
}
</style>
